/**
 * Wellness API Service
 * Handles wellness check-in and sentiment analytics API calls
 */
import apiClient from './config';

export interface WellnessAnswer {
  questionId: string;
  prompt: string;
  response?: string;
  choice?: string;
}

export interface WellnessCheckIn {
  id: string;
  userId: number;
  companyId: number;
  departmentId?: number;
  date: string;
  mood: 'very_negative' | 'negative' | 'neutral' | 'positive' | 'very_positive';
  score: number;
  answers: WellnessAnswer[];
  suggestions: string[];
  riskFlags: string[];
  riskScore: number;
  sentimentModel?: string;
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface WellnessStatus {
  todayCompleted: boolean;
  lastCompletedAt: string | null;
  date: string;
  timezone: string;
}

export interface WellnessSummary {
  series: Array<{
    date: string;
    mood?: string;
    score: number;
    riskScore?: number;
  }>;
  avgScore: number;
  trend: number;
}

export interface AtRiskUser {
  id: string;
  userId: number;
  date: string;
  mood: string;
  score: number;
  riskScore: number;
  riskFlags: string[];
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    departmentId?: number;
  };
  department?: {
    id: number;
    name: string;
  };
}

/**
 * Check if user has completed wellness check-in today
 */
export const checkStatus = async (): Promise<WellnessStatus> => {
  const response = await apiClient.get('/wellness/status');
  return response.data.data;
};

/**
 * Submit wellness check-in
 */
export const submitCheckIn = async (data: {
  mood: string;
  answers: WellnessAnswer[];
  date?: string;
  source?: string;
}): Promise<{
  checkIn: WellnessCheckIn;
  suggestions: string[];
  riskScore: number;
  message: string;
}> => {
  const response = await apiClient.post('/wellness/checkin', data);
  return response.data.data;
};

/**
 * Get user's wellness summary
 */
export const getUserSummary = async (range: string = '7d'): Promise<WellnessSummary> => {
  const response = await apiClient.get(`/wellness/me/summary?range=${range}`);
  return response.data.data;
};

/**
 * Get team sentiment summary (admin only)
 */
export const getTeamSentiment = async (
  departmentId?: number,
  range: string = '30d'
): Promise<WellnessSummary> => {
  const params = new URLSearchParams({ range });
  if (departmentId) {
    params.append('departmentId', departmentId.toString());
  }
  const response = await apiClient.get(`/wellness/admin/team-sentiment?${params.toString()}`);
  return response.data.data;
};

/**
 * Get organization sentiment summary (admin only)
 */
export const getOrgSentiment = async (range: string = '30d'): Promise<WellnessSummary> => {
  const response = await apiClient.get(`/wellness/admin/org-sentiment?range=${range}`);
  return response.data.data;
};

/**
 * Get at-risk users (admin only)
 */
export const getAtRiskUsers = async (limit: number = 50): Promise<AtRiskUser[]> => {
  const response = await apiClient.get(`/wellness/admin/at-risk?limit=${limit}`);
  return response.data.data;
};

const wellnessAPI = {
  checkStatus,
  submitCheckIn,
  getUserSummary,
  getTeamSentiment,
  getOrgSentiment,
  getAtRiskUsers,
  
  getCheckInHistory: async () => {
    const response = await apiClient.get('/wellness/history');
    return response.data.data;
  },

  getCheckInById: async (id: number) => {
    const response = await apiClient.get(`/wellness/check-ins/${id}`);
    return response.data.data;
  },

  getAnalytics: async () => {
    const response = await apiClient.get('/wellness/analytics');
    return response.data.data;
  }
};

export default wellnessAPI;
